/**
 * Tartı Senkronu
 *
 * İki tarafı yan yana gösteren senkron ekranı: solda GTS'teki kg ürünleri,
 * sağda cihazın hafızasındaki kayıtlar. Tartı yazılımlarının ortak kalıbı;
 * "gönder ve bitir" akışının aksine ne gönderildiğini ve cihazda ne olduğunu
 * görmeden iş yapılmıyor.
 *
 * EŞLEŞTİRME ITEMCODE ÜZERİNDEN. Ağırlık barkodunun ortasına basılan kod bu;
 * kasa ürünü ona göre buluyor. Cihazdaki PLU numarası ayrı bir kavram (tartıda
 * tuşla çağırmak için) ve mevcut kayıtlarda ikisi farklı olabiliyor.
 *
 * Bu yüzden güncelleme, eşleşen kaydın **kendi PLU numarasına** yazılıyor.
 * Yoksa cihazda aynı ürünün iki kaydı oluşur: biri eski PLU'da, biri yenide.
 * Cihazda hiç yoksa PLU numarası olarak tartı kodu kullanılıyor — yeni
 * kodlar 1'den başladığı için ikisi doğal olarak aynı sayı oluyor.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ScaleIcon from '@mui/icons-material/MonitorWeight';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/CloudDownload';
import SendIcon from '@mui/icons-material/Send';
import TagIcon from '@mui/icons-material/Tag';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import { scaleApi, ScaleDto } from '../../../../services/scaleService';
import {
  deletePluFromScale,
  MAX_SCAN_RANGE,
  ScalePluRecord,
  ScaleTarget,
  scanScalePlus,
  sendPluToScale,
} from '../../../../services/localScaleService';
import { InventoryService, ScaleCandidate } from '../services/inventoryService';
import { getVatIncludedPrice, formatMoney } from '../../../labelPrinting/dataSources/inventoryLabelSource';
import GuideButton from '../../../common/guide/GuideButton';

interface ScaleSyncDialogProps {
  open: boolean;
  onClose: () => void;
  /** Ekran açılırken bu ürünler işaretli gelsin (stok ekranındaki seçim). */
  preselectedIds?: string[];
}

/** Ürünün seçili tartılar karşısındaki durumu. */
type RowStatus = 'nocode' | 'unknown' | 'current' | 'stale' | 'missing';

const STATUS_LABEL: Record<RowStatus, string> = {
  nocode: 'Kod yok',
  unknown: 'Okunmadı',
  current: 'Güncel',
  stale: 'Farklı',
  missing: 'Cihazda yok',
};

const STATUS_COLOR: Record<RowStatus, 'default' | 'success' | 'warning' | 'error'> = {
  nocode: 'error',
  unknown: 'default',
  current: 'success',
  stale: 'warning',
  missing: 'default',
};

function toItemCode(raw?: string | null): number | null {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return null;
  const value = parseInt(digits, 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function toTarget(scale: ScaleDto): ScaleTarget {
  return { host: scale.host, port: scale.port, model: scale.model };
}

function pricesEqual(a: number, b: number): boolean {
  return Math.round(a * 100) === Math.round(b * 100);
}

/** Cihaz adı 40 karakterde kesiliyor; karşılaştırma da kesilmiş hâl üzerinden. */
function namesEqual(gts: string, device: string): boolean {
  return gts.trim().slice(0, 40).toLocaleLowerCase('tr') === device.trim().toLocaleLowerCase('tr');
}

const ScaleSyncDialog: React.FC<ScaleSyncDialogProps> = ({ open, onClose, preselectedIds }) => {
  const [scales, setScales] = useState<ScaleDto[]>([]);
  const [selectedScaleIds, setSelectedScaleIds] = useState<number[]>([]);
  const [products, setProducts] = useState<ScaleCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [scanTo, setScanTo] = useState(200);
  const [scanning, setScanning] = useState(false);
  const [scanLabel, setScanLabel] = useState('');
  const [deviceData, setDeviceData] = useState<Record<number, ScalePluRecord[]>>({});
  const [scanErrors, setScanErrors] = useState<Record<number, string>>({});
  const [activeTab, setActiveTab] = useState(0);

  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await InventoryService.getScaleCandidates();
      setProducts(list);
    } catch (err: any) {
      setLoadError(err?.response?.data?.message || err?.message || 'Ürün listesi alınamadı.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    setSearch('');
    setPage(0);
    setDeviceData({});
    setScanErrors({});
    setActiveTab(0);
    setNotice(null);
    setActionError(null);
    setProgress(0);
    setPicked(preselectedIds || []);

    loadProducts();

    scaleApi
      .getAll({ activeOnly: true })
      .then((list) => {
        setScales(list);
        setSelectedScaleIds(list.map((s) => s.id));
      })
      .catch((err: any) => setLoadError(err?.message || 'Tartı listesi alınamadı.'));
  }, [open, preselectedIds, loadProducts]);

  const selectedScales = useMemo(
    () => scales.filter((s) => selectedScaleIds.includes(s.id)),
    [scales, selectedScaleIds]
  );

  /** Cihaz kayıtları itemCode ile indeksleniyor: eşleştirme bunun üzerinden. */
  const deviceIndex = useMemo(() => {
    const index: Record<number, Map<number, ScalePluRecord>> = {};
    for (const [scaleId, list] of Object.entries(deviceData)) {
      const map = new Map<number, ScalePluRecord>();
      for (const record of list) map.set(record.itemCode, record);
      index[Number(scaleId)] = map;
    }
    return index;
  }, [deviceData]);

  const scanned = Object.keys(deviceData).length > 0;

  const rows = useMemo(() => {
    return products.map((item) => {
      const code = toItemCode(item.scaleCode);
      const price = getVatIncludedPrice(item as any);
      const shelfLife = item.shelfLifeDays || 0;

      let status: RowStatus;
      const details: string[] = [];

      if (code === null) {
        status = 'nocode';
      } else if (!scanned || selectedScales.length === 0) {
        status = 'unknown';
      } else {
        let missing = 0;
        let stale = 0;

        for (const scale of selectedScales) {
          const record = deviceIndex[scale.id]?.get(code);
          if (!record) {
            missing++;
            continue;
          }
          const diffs: string[] = [];
          if (record.price === null || !pricesEqual(record.price, price)) {
            diffs.push(`fiyat ${record.price === null ? '?' : formatMoney(record.price)}`);
          }
          if (!namesEqual(item.name, record.name)) diffs.push('ad');
          if ((record.sellByDays || 0) !== shelfLife) diffs.push('raf ömrü');

          if (diffs.length > 0) {
            stale++;
            if (selectedScales.length > 1) details.push(`${scale.name}: ${diffs.join(', ')}`);
            else details.push(diffs.join(', '));
          }
        }

        if (missing > 0) status = 'missing';
        else if (stale > 0) status = 'stale';
        else status = 'current';

        if (missing > 0 && selectedScales.length > 1) {
          details.unshift(`${missing} tartıda yok`);
        }
      }

      return { item, code, price, shelfLife, status, detail: details.join(' · ') };
    });
  }, [products, selectedScales, deviceIndex, scanned]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter(({ item }) =>
      item.name.toLocaleLowerCase('tr').includes(term) ||
      String(item.stockCode || '').toLocaleLowerCase('tr').includes(term) ||
      String(item.barcode || '').includes(term) ||
      String(item.scaleCode || '').includes(term)
    );
  }, [rows, search]);

  const pagedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const noCodeRows = useMemo(() => rows.filter((r) => r.status === 'nocode'), [rows]);
  const needsSyncRows = useMemo(
    () => rows.filter((r) => r.status === 'stale' || r.status === 'missing'),
    [rows]
  );

  const pickedRows = useMemo(
    () => rows.filter((r) => picked.includes(r.item.id) && r.code !== null),
    [rows, picked]
  );

  const togglePick = (id: string) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const togglePagePick = () => {
    const ids = pagedRows.map((r) => r.item.id);
    const allPicked = ids.every((id) => picked.includes(id));
    setPicked((prev) => (allPicked
      ? prev.filter((id) => !ids.includes(id))
      : Array.from(new Set([...prev, ...ids]))));
  };

  const toggleScale = (id: number) => {
    setSelectedScaleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ─── Cihazları oku ───────────────────────────────────────────────

  const handleScan = useCallback(async () => {
    if (selectedScales.length === 0) return;

    setScanning(true);
    setActionError(null);
    setNotice(null);
    setProgress(0);

    const collected: Record<number, ScalePluRecord[]> = {};
    const errors: Record<number, string> = {};
    let done = 0;

    // Cihazlar paralel: her biri kendi TCP bağlantısını kullanıyor.
    await Promise.all(selectedScales.map(async (scale) => {
      setScanLabel(scale.name);
      try {
        const result = await scanScalePlus(toTarget(scale), 1, scanTo);
        collected[scale.id] = result.found;
      } catch (err: any) {
        errors[scale.id] = err?.message || 'Okunamadı';
        collected[scale.id] = [];
      } finally {
        done++;
        setProgress(Math.round((done / selectedScales.length) * 100));
      }
    }));

    setDeviceData(collected);
    setScanErrors(errors);
    setScanning(false);
    setScanLabel('');

    const total = Object.values(collected).reduce((sum, list) => sum + list.length, 0);
    setNotice(
      selectedScales.length === 1
        ? `Cihazda ${total} kayıt bulundu (1–${scanTo} arası tarandı).`
        : `${selectedScales.length} tartıda toplam ${total} kayıt bulundu.`
    );
  }, [selectedScales, scanTo]);

  // ─── Tartı kodu ver ──────────────────────────────────────────────

  const handleAssignCodes = useCallback(async () => {
    if (noCodeRows.length === 0) return;

    setBusy(true);
    setBusyLabel('Kodlar atanıyor…');
    setActionError(null);
    setNotice(null);
    try {
      const result = await InventoryService.assignScaleCodes(noCodeRows.map((r) => r.item.id));

      if (result.assigned.length > 0) {
        const codes = new Map(result.assigned.map((a) => [a.id, a.scaleCode]));
        setProducts((prev) => prev.map((p) => (
          codes.has(p.id) ? { ...p, scaleCode: codes.get(p.id), isScale: true } : p
        )));
        const first = result.assigned[0].scaleCode;
        const last = result.assigned[result.assigned.length - 1].scaleCode;
        setNotice(result.assigned.length === 1
          ? `${result.assigned[0].name} → kod ${first} (tartıda PLU ${parseInt(first, 10)})`
          : `${result.assigned.length} ürüne kod atandı: ${first} – ${last}`);
      } else {
        setNotice('Kod atanacak ürün bulunamadı.');
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Kodlar atanamadı.');
    } finally {
      setBusy(false);
      setBusyLabel('');
    }
  }, [noCodeRows]);

  // ─── Gönderim ────────────────────────────────────────────────────

  const sendRows = useCallback(async (
    targetRows: Array<{ item: ScaleCandidate; code: number | null; price: number; shelfLife: number }>,
    label: string
  ) => {
    const jobs = targetRows.filter((r) => r.code !== null);
    if (jobs.length === 0 || selectedScales.length === 0) return;

    setBusy(true);
    setBusyLabel(label);
    setActionError(null);
    setNotice(null);
    setProgress(0);

    let ok = 0;
    const failures: string[] = [];
    const total = jobs.length * selectedScales.length;
    let done = 0;

    // Cihazlar paralel, cihaz içi sıralı: tartı komutları kuyrukta işleniyor.
    await Promise.all(selectedScales.map(async (scale) => {
      for (const row of jobs) {
        const code = row.code as number;
        // Cihazda zaten varsa kendi PLU numarasına yaz; yoksa kod = PLU.
        const existing = deviceIndex[scale.id]?.get(code);
        try {
          await sendPluToScale(toTarget(scale), {
            pluNumber: existing ? existing.pluNumber : code,
            itemCode: code,
            name: row.item.name,
            price: row.price,
            sellByDays: row.shelfLife,
            department: 1,
          });
          ok++;
        } catch (err: any) {
          failures.push(`${row.item.name}${selectedScales.length > 1 ? ` (${scale.name})` : ''}: ${err?.message || 'hata'}`);
        } finally {
          done++;
          setBusyLabel(`${label} — ${row.item.name}`);
          setProgress(Math.round((done / total) * 100));
        }
      }
    }));

    setBusy(false);
    setBusyLabel('');

    if (failures.length === 0) {
      setNotice(`${ok} kayıt gönderildi. Doğrulamak için cihazları yeniden okuyun.`);
    } else {
      setNotice(ok > 0 ? `${ok} kayıt gönderildi.` : null);
      setActionError(`${failures.length} kayıt gönderilemedi — ${failures.slice(0, 3).join(' | ')}`);
    }
  }, [selectedScales, deviceIndex]);

  // ─── Cihazdan silme ──────────────────────────────────────────────

  /**
   * Silme her zaman tek tek ve kullanıcının gördüğü liste üzerinden.
   * Cihazın "hepsini sil" komutu bilerek uygulanmadı: geri dönüşü yok.
   */
  const deleteRecords = useCallback(async (scale: ScaleDto, records: ScalePluRecord[]) => {
    if (records.length === 0) return;

    setBusy(true);
    setBusyLabel('Cihazdan siliniyor…');
    setActionError(null);
    setNotice(null);
    setProgress(0);

    const removed: number[] = [];
    const failures: string[] = [];

    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      setBusyLabel(`Siliniyor: ${record.name}`);
      try {
        await deletePluFromScale(toTarget(scale), record.pluNumber);
        removed.push(record.pluNumber);
      } catch (err: any) {
        failures.push(`${record.name}: ${err?.message || 'hata'}`);
      }
      setProgress(Math.round(((i + 1) / records.length) * 100));
    }

    // Silinenleri listeden düşürüyoruz; yeniden taramaya gerek kalmıyor.
    if (removed.length > 0) {
      setDeviceData((prev) => ({
        ...prev,
        [scale.id]: (prev[scale.id] || []).filter((r) => !removed.includes(r.pluNumber)),
      }));
    }

    setBusy(false);
    setBusyLabel('');

    if (failures.length === 0) {
      setNotice(`${removed.length} kayıt cihazdan silindi.`);
    } else {
      setActionError(`${failures.length} kayıt silinemedi — ${failures.slice(0, 3).join(' | ')}`);
    }
  }, []);

  // ─── Görünüm ─────────────────────────────────────────────────────

  const activeScale = selectedScales[activeTab] || selectedScales[0];
  const activeRecords = activeScale ? (deviceData[activeScale.id] || []) : [];

  /** Cihazda olup GTS'te karşılığı olmayan kayıtlar. */
  const gtsCodes = useMemo(() => {
    const set = new Set<number>();
    for (const { code } of rows) if (code !== null) set.add(code);
    return set;
  }, [rows]);

  const orphans = useMemo(
    () => activeRecords.filter((r) => !gtsCodes.has(r.itemCode)),
    [activeRecords, gtsCodes]
  );

  const renderScaleStrip = () => (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}
    >
      <Typography variant="body2" fontWeight={600}>Tartılar:</Typography>

      {scales.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Tanımlı tartı yok — Cihaz Yönetimi › Tartılar ekranından ekleyin.
        </Typography>
      )}

      {scales.map((scale) => (
        <Chip
          key={scale.id}
          label={`${scale.name} · ${scale.host}`}
          color={selectedScaleIds.includes(scale.id) ? 'primary' : 'default'}
          variant={selectedScaleIds.includes(scale.id) ? 'filled' : 'outlined'}
          onClick={() => toggleScale(scale.id)}
          size="small"
        />
      ))}

      <Box sx={{ flex: 1 }} />

      <TextField
        size="small"
        type="number"
        label="Tara: 1 –"
        value={scanTo}
        onChange={(e) => {
          const value = parseInt(e.target.value, 10);
          setScanTo(Math.min(MAX_SCAN_RANGE, Math.max(1, Number.isFinite(value) ? value : 1)));
        }}
        sx={{ width: 120 }}
        inputProps={{ min: 1, max: MAX_SCAN_RANGE }}
      />

      <Button
        variant="outlined"
        size="small"
        startIcon={scanning ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={handleScan}
        disabled={scanning || busy || selectedScales.length === 0}
      >
        Cihazları Oku
      </Button>
    </Stack>
  );

  const renderProductPanel = () => (
    <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Ürün, stok kodu, barkod veya tartı kodu ara…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        {noCodeRows.length > 0 && (
          <Tooltip title="Tartı kodu boş olan ürünlere 1'den başlayarak boştaki en küçük numarayı atar">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<TagIcon />}
                onClick={handleAssignCodes}
                disabled={busy}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {noCodeRows.length} ürüne kod ver
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Box sx={{ flex: 1, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={pagedRows.length > 0 && pagedRows.every((r) => picked.includes(r.item.id))}
                  indeterminate={
                    pagedRows.some((r) => picked.includes(r.item.id)) &&
                    !pagedRows.every((r) => picked.includes(r.item.id))
                  }
                  onChange={togglePagePick}
                />
              </TableCell>
              <TableCell>Ürün</TableCell>
              <TableCell align="right">PLU / Kod</TableCell>
              <TableCell align="right">Fiyat</TableCell>
              <TableCell>Durum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map(({ item, code, price, status, detail }) => (
              <TableRow key={item.id} hover selected={picked.includes(item.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={picked.includes(item.id)}
                    onChange={() => togglePick(item.id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap title={item.name}>{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.stockCode || '—'}
                    {item.shelfLifeDays ? ` · raf ${item.shelfLifeDays} gün` : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {code === null ? (
                    <Typography variant="caption" color="error">yok</Typography>
                  ) : (
                    <>
                      <Typography variant="body2">{code}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {String(code).padStart(5, '0')}
                      </Typography>
                    </>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatMoney(price)} ₺</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={STATUS_LABEL[status]} color={STATUS_COLOR[status]} />
                  {detail && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {detail}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {!loading && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }} align="center">
                    {products.length === 0
                      ? 'Birimi kg olan aktif ürün yok. Tartıya yalnızca kg ürünler gönderilir.'
                      : 'Aramaya uyan ürün yok.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_e, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Satır"
      />
    </Box>
  );

  const renderDevicePanel = () => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Cihazdaki kayıtlar</Typography>

      {selectedScales.length > 1 && (
        <Tabs
          value={Math.min(activeTab, selectedScales.length - 1)}
          onChange={(_e, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 36, mb: 1 }}
        >
          {selectedScales.map((scale) => (
            <Tab
              key={scale.id}
              label={`${scale.name} (${(deviceData[scale.id] || []).length})`}
              sx={{ minHeight: 36, py: 0 }}
            />
          ))}
        </Tabs>
      )}

      {activeScale && scanErrors[activeScale.id] && (
        <Alert severity="error" sx={{ mb: 1 }}>{scanErrors[activeScale.id]}</Alert>
      )}

      {!scanned && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Cihazın hafızasını görmek için yukarıdan <b>Cihazları Oku</b> deyin. Okuma
          yapılmadan hangi ürünün tartıda güncel olduğu bilinemez.
        </Alert>
      )}

      {scanned && orphans.length > 0 && activeScale && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button
              size="small"
              color="inherit"
              disabled={busy || scanning}
              onClick={() => deleteRecords(activeScale, orphans)}
            >
              Hepsini sil
            </Button>
          }
        >
          {orphans.length} kayıt cihazda var ama GTS'te karşılığı yok. Bunlar eski
          ürünler olabilir; kasada yanlış fiyat okunmaması için temizlenmeli.
        </Alert>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="right">PLU</TableCell>
              <TableCell align="right">Kod</TableCell>
              <TableCell>Ad</TableCell>
              <TableCell align="right">Fiyat</TableCell>
              <TableCell>Eşleşme</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {activeRecords.map((record) => {
              const matched = gtsCodes.has(record.itemCode);
              return (
                <TableRow key={`${record.pluNumber}-${record.itemCode}`} hover>
                  <TableCell align="right">{record.pluNumber}</TableCell>
                  <TableCell align="right">
                    {String(record.itemCode).padStart(5, '0')}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap title={record.name}>{record.name}</Typography>
                    {record.sellByDays ? (
                      <Typography variant="caption" color="text.secondary">
                        raf {record.sellByDays} gün
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {record.price === null ? '—' : `${formatMoney(record.price)} ₺`}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={matched ? 'GTS ürünü' : 'GTS\'te yok'}
                      color={matched ? 'success' : 'warning'}
                      variant={matched ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Tooltip title={`PLU ${record.pluNumber} kaydını cihazdan sil`}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={busy || scanning || !activeScale}
                          onClick={() => activeScale && deleteRecords(activeScale, [record])}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {scanned && activeRecords.length === 0 && !(activeScale && scanErrors[activeScale.id]) && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }} align="center">
                    1–{scanTo} aralığında kayıt bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={busy || scanning ? undefined : onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '92vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <ScaleIcon color="primary" />
        {/* DialogTitle zaten h2 basıyor; component vermezsek h6 iç içe giriyor. */}
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>Tartı Senkronu</Typography>
        <GuideButton guideKey="inventory.scaleSync" size="small" />
        <IconButton size="small" onClick={onClose} disabled={busy || scanning}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      {renderScaleStrip()}
      {(scanning || busy) && (
        <Box sx={{ px: 2, py: 0.5 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary">
            {scanning ? `Okunuyor: ${scanLabel}` : busyLabel}
          </Typography>
        </Box>
      )}
      <Divider />

      <DialogContent sx={{ display: 'flex', gap: 2, overflow: 'hidden', p: 2 }}>
        {loadError && <Alert severity="error">{loadError}</Alert>}

        {!loadError && (
          <>
            {renderProductPanel()}
            <Divider orientation="vertical" flexItem />
            {renderDevicePanel()}
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ gap: 1, px: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          {actionError && <Alert severity="error" sx={{ py: 0 }}>{actionError}</Alert>}
          {!actionError && notice && <Alert severity="success" sx={{ py: 0 }}>{notice}</Alert>}
          {!actionError && !notice && (
            <Typography variant="caption" color="text.secondary">
              {products.length} kg ürün · {picked.length} seçili
              {scanned ? ` · ${needsSyncRows.length} ürün güncel değil` : ''}
            </Typography>
          )}
        </Box>

        <Button onClick={onClose} disabled={busy || scanning}>Kapat</Button>

        <Tooltip title={scanned ? '' : 'Önce cihazları okuyun'}>
          <span>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => sendRows(needsSyncRows, 'Farklı olanlar gönderiliyor')}
              disabled={busy || scanning || !scanned || needsSyncRows.length === 0}
            >
              Farklı olanları gönder ({needsSyncRows.length})
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={() => sendRows(pickedRows, 'Seçilenler gönderiliyor')}
          disabled={busy || scanning || pickedRows.length === 0 || selectedScales.length === 0}
        >
          {selectedScales.length > 1
            ? `${pickedRows.length} ürünü ${selectedScales.length} tartıya gönder`
            : `${pickedRows.length} ürünü gönder`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScaleSyncDialog;
